// src/components/EINApplication.js
// EIN Application UI — SS-4 form faxed to IRS

import React, { useState, useEffect, useCallback } from 'react';
import useFilingGenerator from '../hooks/useFilingGenerator';
import { FILING_STATUS } from '../hooks/usePlanTracker';
import { FilingPreview } from './FilingPreview';

const STEP_FORM    = 'form';
const STEP_PREVIEW = 'preview';
const STEP_RESULT  = 'result';

// Filing Summary fields shown in the FilingPreview side panel — same
// component/CSS as Form 5472's review screen, different field set since
// SS-4 has no EIN/tax year yet (that's literally what it's applying for).
const EIN_SUMMARY_FIELDS = [
  { label: 'LLC',     key: 'llcName'        },
  { label: 'Owner',   key: 'ownerName'      },
  { label: 'State',   key: 'formationState' },
  { label: 'Country', key: 'ownerCountry'   },
];

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia','Brazil',
  'Bulgaria','Canada','Chile','China','Colombia','Croatia','Czech Republic',
  'Denmark','Egypt','Finland','France','Georgia','Germany','Ghana','Greece',
  'Hungary','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Japan','Jordan','Kazakhstan','Kenya','Kuwait','Latvia','Lebanon','Lithuania',
  'Malaysia','Mexico','Morocco','Netherlands','New Zealand','Nigeria','Norway',
  'Pakistan','Panama','Peru','Philippines','Poland','Portugal','Qatar','Romania',
  'Russia','Saudi Arabia','Serbia','Singapore','South Africa','South Korea',
  'Spain','Sri Lanka','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'Ukraine','United Arab Emirates','United Kingdom','Uruguay','Uzbekistan',
  'Venezuela','Vietnam',
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

// `category` here MUST match a key in LINE16_CHECKBOX_MAP in server/ein.js —
// that's what drives the Line 16 checkbox. Most of these don't have a real
// IRS category (SS-4's 12 buckets are old Census industry groups — no
// "software" or "professional services" bucket exists), so they fall back
// to "other" and rely on Line 16's free-text specify box (filled from
// `activity`) plus the Line 17 description to actually describe the business.
// E-commerce is the one preset with a genuine IRS category match: Retail.
const BUSINESS_PRESETS = [
  { label: 'Consulting / Advisory',         activity: 'Consulting Services',              code: '541690', category: 'other'  },
  { label: 'Software / IT Services',        activity: 'Software & IT Services',           code: '541511', category: 'other'  },
  { label: 'SaaS / Software Product',       activity: 'Software Publishing',              code: '511210', category: 'other'  },
  { label: 'Digital Marketing / Agency',    activity: 'Advertising & Marketing Services', code: '541810', category: 'other'  },
  { label: 'E-commerce / Online Retail',    activity: 'Electronic Shopping & Retail',     code: '454110', category: 'retail' },
  { label: 'Freelance / Creative Services', activity: 'Independent Artists & Writers',    code: '711510', category: 'other'  },
  { label: 'Other / General Business',      activity: 'Other Professional Services',      code: '541990', category: 'other'  },
];

const EMPTY = {
  llcName: '', ownerName: '', ownerEmail: '',
  ownerAddress: '', ownerCity: '', ownerState: '', ownerZip: '', ownerCountry: '',
  businessActivity: '', businessCode: '', businessCategory: '', formationState: 'Delaware',
  formationDate: '', closingMonth: 'December',
};

export function EINApplication({ planTracker, loadedRecord, onClearLoadedRecord }) {
  const {
    isProcessing,
    processingError,
    faxConfirmation,
    generatedPdfBase64,
    filingData,
    generateToolPDF,
    sendToolPDF,
    downloadPDF,
    clearAll,
  } = useFilingGenerator(planTracker);

  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(STEP_FORM);

  // A fresh submission always wins over a loaded historical one.
  const confirmation = faxConfirmation || loadedRecord?.faxConfirmation || null;
  const pdfBase64     = generatedPdfBase64 || loadedRecord?.pdfBase64 || null;

  useEffect(() => {
    // loadedRecord only ever exists for a completed (already-faxed) filing
    // reopened from sidebar history — go straight to the result screen.
    if (loadedRecord?.formData) {
      setForm({ ...EMPTY, ...loadedRecord.formData });
      setStep(STEP_RESULT);
    }
  }, [loadedRecord]);

  const handleNew = () => {
    clearAll();
    setForm(EMPTY);
    setStep(STEP_FORM);
    onClearLoadedRecord?.();
  };

  const handle = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handlePreset = (e) => {
    const preset = BUSINESS_PRESETS.find(p => p.code === e.target.value);
    if (!preset) return;
    setForm(prev => ({
      ...prev,
      businessActivity: preset.activity,
      businessCode:     preset.code,
      businessCategory: preset.category,
    }));
  };

  // Step 1 → 2: build the SS-4 PDF and move to the review/sign screen.
  // This is the point the filing credit gets consumed — same rule as the
  // 5472 flow, where parseStatement (the first real step) consumes it.
  const handleFormSubmit = async () => {
    try {
      await generateToolPDF('/api/ein/generate', form, { checkCredit: true });
      setStep(STEP_PREVIEW);
    } catch {
      // processingError is already set by the hook
    }
  };

  // Re-builds the PDF with whatever's in updatedFormData (used to bake the
  // signature in right before sending — no credit re-check/re-consume).
  const handleRegeneratePDF = useCallback(async (_transactions, updatedFormData) => {
    const data = await generateToolPDF('/api/ein/generate', updatedFormData, { checkCredit: false });
    return data?.pdfBase64;
  }, [generateToolPDF]);

  // Step 2 → 3: regenerate with the signature baked in, then fax exactly
  // that PDF. Mirrors FilingGenerator.js's handleSendToIRS for Form 5472.
  const handleSendToIRS = async (updatedFormData) => {
    try {
      const pdfToSend = await handleRegeneratePDF(null, updatedFormData) || generatedPdfBase64;

      await sendToolPDF('/api/ein/send', {
        pdfBase64:  pdfToSend,
        llcName:    updatedFormData.llcName,
        ownerEmail: updatedFormData.ownerEmail,
      }, {
        type:       'ein',
        status:     FILING_STATUS.FILED,
        formData:   updatedFormData,
        buildTitle: (p) => `${p.llcName || 'LLC'} — EIN Application`,
      });

      setForm(updatedFormData);
      setStep(STEP_RESULT);
    } catch {
      // processingError is already set by the hook
    }
  };

  const isValid =
    form.llcName && form.ownerName && form.ownerEmail &&
    form.ownerCountry && form.businessActivity && form.businessCategory;

  // ── PREVIEW / REVIEW & SIGN SCREEN ────────────────────────────────────────
  // Same FilingPreview component and CSS as Form 5472's review step —
  // overlayFields is [] (SS-4 values are locked in from the form already,
  // nothing to click-edit inline), so the Edit Fields affordance just
  // doesn't render, and showTxSummary is false since there are no
  // transactions in an EIN application.
  if (step === STEP_PREVIEW) {
    return (
      <FilingPreview
        filingData={filingData || form}
        transactions={[]}
        setTransactions={() => {}}
        generatedPdfBase64={generatedPdfBase64}
        isProcessing={isProcessing}
        processingError={processingError}
        onSendToIRS={handleSendToIRS}
        onBack={() => setStep(STEP_FORM)}
        onDownload={() => downloadPDF(generatedPdfBase64, `SS-4-EIN-Application-${form.llcName || 'LLC'}.pdf`)}
        onRegeneratePDF={handleRegeneratePDF}
        title={`SS-4 EIN Application — ${form.llcName || 'LLC'}`}
        docBadge="IRS Form SS-4"
        overlayFields={[]}
        showTxSummary={false}
        summaryFields={EIN_SUMMARY_FIELDS}
        sendLabel="Send to IRS via Fax"
        sendingLabel="Sending to IRS…"
        footerNote="Faxed directly to IRS · SS-4 EIN Application"
        confirmText="I confirm this application is accurate and authorise submission to the IRS"
      />
    );
  }

  // ── CONFIRMATION SCREEN ───────────────────────────────────────────────────
  // Renders for a fresh submission AND when reopened from sidebar history.
  if (step === STEP_RESULT && confirmation) {
    return (
      <div className="form-page">
        <div className="form-page-inner">

          <div className="fr-status-card fr-status-card--faxed" style={{ marginBottom: 20 }}>
            <div className="fr-status-icon fr-status-icon--faxed">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                  19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3
                  a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09
                  9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0
                  2.81.7A2 2 0 0 1 22 16.92z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="fr-status-text">
              <div className="fr-status-headline">SS-4 Faxed to IRS</div>
              <div className="fr-status-sub">
                Faxed at {new Date(confirmation.faxedAt).toLocaleString()} · Awaiting IRS processing
              </div>
            </div>
          </div>

          <div className="fr-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div className="fr-card">
                <div className="fr-card-title">Fax Receipt</div>
                <div className="fr-detail-list">
                  {[
                    ['Fax ID',   confirmation.faxId],
                    ['Sent to',  confirmation.sentTo],
                    ['Pages',    confirmation.pages],
                    ['Status',   confirmation.status],
                  ].map(([label, value]) => (
                    <div className="fr-detail-row" key={label}>
                      <span className="fr-detail-label">{label}</span>
                      <span className="fr-detail-value">{value}</span>
                    </div>
                  ))}
                </div>
                <p className="fr-note">{confirmation.note}</p>
              </div>

              <div className="fr-actions" style={{ marginTop: 16 }}>
                {pdfBase64 && (
                  <button className="fr-btn-primary"
                    onClick={() => downloadPDF(
                      pdfBase64,
                      `SS-4-EIN-Application-${form.llcName || 'LLC'}.pdf`
                    )}
                    type="button">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download SS-4 PDF
                  </button>
                )}
                <button className="fr-btn-secondary"
                  onClick={handleNew}
                  type="button">
                  New Application
                </button>
              </div>
            </div>

            <div>
              <div className="fr-card">
                <div className="fr-card-title">Next Steps</div>
                <div className="fr-next-list">
                  {(confirmation.nextSteps || []).map((step, i) => (
                    <div className="fr-next-item" key={i}>
                      <div className="fr-next-num">{i + 1}</div>
                      <div className="fr-next-text">{step}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // ── FORM ──────────────────────────────────────────────────────────────────
  return (
    <div className="form-page">
      <div className="form-page-inner">

        <div className="form-hero">
          <div className="form-hero-left">
            <div className="form-eyebrow">
              <span className="eyebrow-pulse" />
              IRS Form SS-4 · EIN Application
            </div>
            <h1 className="form-title">
              Get your EIN <em>without the IRS hold music</em>
            </h1>
            <p className="form-subtitle">
              We fill your SS-4 and fax it directly to the IRS.
              No phone calls. No 2-hour hold times. Just wait for your EIN by mail.
            </p>
          </div>
        </div>

        <div className="form-top-grid">

          {/* LLC Panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Your LLC</span>
            </div>
            <div className="form-panel-body">

              <div className="form-group">
                <label className="form-label">LLC legal name</label>
                <input className="form-input" type="text"
                  placeholder="Acme Solutions LLC"
                  value={form.llcName} onChange={handle('llcName')}
                  disabled={isProcessing} />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">State of formation</label>
                  <select className="form-input form-select"
                    value={form.formationState} onChange={handle('formationState')}
                    disabled={isProcessing}>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Formation date</label>
                  <input className="form-input" type="date"
                    value={form.formationDate} onChange={handle('formationDate')}
                    disabled={isProcessing} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">What does your LLC do?</label>
                <select className="form-input form-select"
                  value={form.businessCode} onChange={handlePreset}
                  disabled={isProcessing}>
                  <option value="">Select business type</option>
                  {BUSINESS_PRESETS.map(p => (
                    <option key={p.code} value={p.code}>{p.label}</option>
                  ))}
                </select>
                {form.businessActivity && (
                  <span className="form-field-hint">
                    Activity: {form.businessActivity} · Code: {form.businessCode}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Fiscal year end</label>
                <select className="form-input form-select"
                  value={form.closingMonth} onChange={handle('closingMonth')}
                  disabled={isProcessing}>
                  {['January','February','March','April','May','June','July',
                    'August','September','October','November','December'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <span className="form-field-hint">Most LLCs use December</span>
              </div>

            </div>
          </div>

          {/* Owner Panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Responsible Party (you)</span>
            </div>
            <div className="form-panel-body">

              <div className="form-group">
                <label className="form-label">Full legal name</label>
                <input className="form-input" type="text"
                  placeholder="Jan Müller"
                  value={form.ownerName} onChange={handle('ownerName')}
                  disabled={isProcessing} />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email"
                  placeholder="you@example.com"
                  value={form.ownerEmail} onChange={handle('ownerEmail')}
                  disabled={isProcessing} />
                <span className="form-field-hint">We'll send your fax confirmation here</span>
              </div>

              <div className="form-group">
                <label className="form-label">Country of residence</label>
                <select className="form-input form-select"
                  value={form.ownerCountry} onChange={handle('ownerCountry')}
                  disabled={isProcessing}>
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Street address</label>
                <input className="form-input" type="text"
                  placeholder="Unter den Linden 12"
                  value={form.ownerAddress} onChange={handle('ownerAddress')}
                  disabled={isProcessing} />
              </div>

              <div className="form-row-3-col">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" type="text"
                    value={form.ownerCity} onChange={handle('ownerCity')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input className="form-input" type="text"
                    value={form.ownerState} onChange={handle('ownerState')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal code</label>
                  <input className="form-input" type="text"
                    value={form.ownerZip} onChange={handle('ownerZip')}
                    disabled={isProcessing} />
                </div>
              </div>

            </div>
          </div>

        </div>

        <div className="form-footer-bar">
          <div className="form-footer-left">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Review and sign your SS-4 before it's faxed to the IRS
          </div>
          <button
            className="btn-generate"
            onClick={handleFormSubmit}
            disabled={isProcessing || !isValid}
            type="button"
          >
            {isProcessing ? (
              <><div className="spinner-small" /> Generating SS-4…</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                    19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3
                    a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09
                    9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0
                    2.81.7A2 2 0 0 1 22 16.92z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Generate &amp; Review SS-4
              </>
            )}
          </button>
        </div>

        {processingError && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {processingError}
          </div>
        )}

      </div>
    </div>
  );
}